import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type DiscAplicacao, type DiscResposta } from "../api";
import {
  FATORES,
  FATOR_INFO,
  LETRAS,
  QUESTOES_DISC,
  TOTAL_QUESTOES,
  faixaDisc,
  leituraDisc,
  type Fator,
  type Letra,
  type OrigemQuestoes,
  type PlacarDisc,
  type QuestaoDiscGabarito,
  type ResultadoDisc,
} from "../disc-modelo";
import { imprimirFolhaDisc, montarFolhaDisc, type DadosFolhaDisc } from "../disc-folha";
import ErrorState from "./ErrorState";
import {
  IconCheck,
  IconClipboard,
  IconClose,
  IconEye,
  IconLink,
  IconPlus,
  IconSearch,
  IconSparkles,
  IconTrash,
} from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * DISC — ANÁLISE DE PERFIL COMPORTAMENTAL (a aba do RH).
 *
 * O caminho é o mesmo da Triagem de Currículo: o RH abre a aplicação do teste
 * (nome, setor, função) → copia o LINK e manda no WhatsApp do candidato → o
 * candidato responde as 20 questões sem login → o SERVIDOR corrige pelo
 * gabarito oficial (server/disc-gabarito.ts) e o resultado aparece aqui.
 *
 * Duas decisões explicam o formato desta tela:
 *
 *  • O gabarito nunca sai para o candidato — e a correção não é refeita aqui: o
 *    placar D/I/S/C vem gravado do servidor, e é ele que a tela mostra e que o
 *    laudo imprime. O modelo é usado só para o TEXTO (nome do fator, faixa,
 *    leitura do resultado e o enunciado de cada questão).
 *
 *  • A IA apenas SUGERE o parecer: o texto cai no campo e só existe de verdade
 *    depois que o RH clicar em "Salvar parecer". Assim ninguém perde o que
 *    escreveu à mão porque clicou em "Parecer com IA" por engano.
 */

/** O link que vai para o candidato (o mesmo link serve para todos). */
function linkPublico(token: string): string {
  return `${window.location.origin}/disc/${token}`;
}

/**
 * Abre a conversa do WhatsApp com o texto já escrito (o RH só confere e envia).
 *
 * Fica fora dos componentes porque são DUAS mensagens diferentes pelo mesmo
 * caminho: o CONVITE (bloco do link, que leva o endereço do teste) e o RETORNO
 * para quem já respondeu — este último NUNCA pode levar o link junto, senão o
 * candidato refaz o teste achando que a primeira resposta se perdeu.
 */
function abrirWhatsApp(telefone: string | null | undefined, texto: string) {
  const numero = String(telefone ?? "").replace(/\D/g, "");
  const base = numero
    ? `https://wa.me/${numero.length <= 11 ? `55${numero}` : numero}`
    : "https://wa.me/";
  window.open(`${base}?text=${encodeURIComponent(texto)}`, "_blank", "noopener");
}

/** "Isaías Gomes da Silva" → "Isaías" (a mensagem do WhatsApp é pessoal). */
function primeiroNome(nome: string): string {
  return String(nome ?? "").trim().split(/\s+/)[0] ?? "";
}

export function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

/**
 * Uma questão como a TELA precisa dela: número, enunciado e alternativas.
 *
 * Serve tanto para as questões da vaga (que trazem o fator junto) quanto para as
 * 20 gerais do papel — o que a tela mostra ao RH e ao candidato é o mesmo nos
 * dois casos.
 */
export type QuestaoExibida = {
  n: number;
  enunciado: string;
  alternativas: { letra: string; texto: string }[];
};

/**
 * O QUESTIONÁRIO QUE VALE PARA ESTA APLICAÇÃO.
 *
 * Com o DISC por cargo, cada teste pode ter as suas questões (escritas pela IA
 * a partir da função e gravadas na aplicação). Quando não tem — o caso de toda
 * aplicação criada antes disso —, valem as 20 do papel. Este é o único lugar da
 * tela que decide isso: o lançamento do cartão de papel, o cartão do
 * respondente e o laudo leem todos daqui, para não mostrarem testes diferentes.
 */
function questoesDoTeste(aplicacao: { questoes?: QuestaoDiscGabarito[] } | null): QuestaoExibida[] {
  const proprias = aplicacao?.questoes ?? [];
  return proprias.length > 0 ? proprias : QUESTOES_DISC;
}

/** O enunciado da questão (a resposta gravada guarda só o número e a letra). */
export function enunciadoDaQuestao(q: number, questoes: QuestaoExibida[]): string {
  return questoes.find((x) => x.n === q)?.enunciado ?? `Questão ${q}`;
}

/** O texto da alternativa marcada — o candidato marcou "C", o RH lê a frase. */
export function textoAlternativa(q: number, letra: string, questoes: QuestaoExibida[]): string {
  const questao = questoes.find((x) => x.n === q);
  const alt = questao?.alternativas.find((a) => a.letra === String(letra).toUpperCase());
  return alt?.texto ?? "— (alternativa fora do gabarito)";
}

/**
 * Monta o ResultadoDisc a partir do que o SERVIDOR gravou.
 *
 * O placar e o perfil não são recalculados na tela de propósito: quem corrige é
 * o servidor, e o laudo tem que dizer exatamente o que está no banco. A ordem
 * (primário/secundário) sai do próprio perfil gravado ("C/S"); só quando ele
 * falta — registro antigo — é que ela é refeita aqui, com o mesmo desempate do
 * gabarito (D → I → S → C).
 */
export function resultadoDaResposta(r: DiscResposta): ResultadoDisc {
  const placar: PlacarDisc = { D: r.d ?? 0, I: r.i ?? 0, S: r.s ?? 0, C: r.c ?? 0 };
  const partes = String(r.perfil ?? "")
    .split("/")
    .map((x) => x.trim().toUpperCase())
    .filter((x): x is Fator => (FATORES as string[]).includes(x));
  const ordenados = [...FATORES].sort((a, b) => placar[b] - placar[a]);

  const primario = partes[0] ?? ordenados[0];
  const secundario =
    partes.length > 0 ? partes[1] ?? null : placar[ordenados[1]] > 0 ? ordenados[1] : null;

  return {
    placar,
    primario,
    secundario,
    perfil: r.perfil ?? (secundario ? `${primario}/${secundario}` : primario),
    perfilNome:
      r.perfil_nome ??
      (secundario
        ? `${FATOR_INFO[primario].nome} com ${FATOR_INFO[secundario].nome}`
        : FATOR_INFO[primario].nome),
    respondidas: (r.respostas ?? []).length,
  };
}

/* ==================== Lista de testes aplicados ==================== */

export default function DiscView() {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [linhas, setLinhas] = useState<DiscAplicacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [nova, setNova] = useState(false);
  const [abrindo, setAbrindo] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setLinhas(await api.disc.listar());
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtradas = useMemo(() => {
    const q = norm(busca);
    if (!q) return linhas;
    return linhas.filter(
      (a) => norm(a.nome).includes(q) || norm(a.setor).includes(q) || norm(a.cargo).includes(q),
    );
  }, [linhas, busca]);

  async function excluir(a: DiscAplicacao) {
    if (
      !confirm(
        `Excluir o teste DISC "${a.nome}"?\n\n` +
          `As respostas dos candidatos e os pareceres também são apagados.`,
      )
    )
      return;
    try {
      await api.disc.remover(a.id);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar teste, setor ou função..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtradas.length} teste(s)</span>
          <button className="btn-primary" onClick={() => setNova(true)}>
            <IconPlus size={17} /> Novo teste DISC
          </button>
        </div>
      </div>

      {aviso && (
        <div className="triagem-aviso">
          {aviso}
          <button className="btn-secondary btn-sm" onClick={() => setAviso(null)}>
            Fechar
          </button>
        </div>
      )}

      {loading ? (
        <div className="empty">Carregando...</div>
      ) : filtradas.length === 0 ? (
        <div className="empty">
          Nenhum teste DISC ainda. Clique em <strong>Novo teste DISC</strong>: informe a vaga e o
          cargo, gere as <strong>questões da função com a IA</strong> (ou use as{" "}
          {TOTAL_QUESTOES} gerais do papel), copie o link e mande no WhatsApp do candidato. A
          correção é automática, pelo gabarito do próprio teste.
        </div>
      ) : (
        <div className="disc-cards">
          {filtradas.map((a) => (
            <article className="disc-card" key={a.id}>
              <header>
                <button className="triagem-nome" onClick={() => setAbrindo(a.id)}>
                  {a.nome}
                </button>
                <span className={`pill ${a.status === "Aberta" ? "pill-ok" : "pill-neutral"}`}>
                  {a.status}
                </span>
              </header>

              <p className="disc-card-meta">
                {[a.setor, a.cargo].filter(Boolean).join(" · ") || "Sem setor ou função informados"}
              </p>

              <div className="disc-card-numeros">
                <span>
                  <strong>{a.respondentes ?? 0}</strong> respondeu(ram)
                </span>
                {/* QUAL QUESTIONÁRIO este teste aplica. Sem isto, um teste
                    escrito para a função e o formulário geral do papel ficam
                    idênticos na lista — e o RH só descobre a diferença abrindo
                    um por um. */}
                <span className="pill pill-neutral">
                  {(a.total_questoes ?? 0) > 0
                    ? `${a.total_questoes} questões da função`
                    : `${TOTAL_QUESTOES} questões (geral)`}
                </span>
                {(a.sem_parecer ?? 0) > 0 && (
                  <span className="pill pill-warn">{a.sem_parecer} sem parecer</span>
                )}
              </div>

              <footer>
                <small className="muted">
                  criado em {quando(a.criado_em)}
                  {a.criado_por ? ` por ${a.criado_por}` : ""}
                </small>
                <div className="disc-card-acoes">
                  <button className="btn-secondary btn-sm" onClick={() => setAbrindo(a.id)}>
                    Abrir
                  </button>
                  {podeExcluir && (
                    <button
                      className="icon-btn icon-btn-danger"
                      title="Excluir teste DISC"
                      onClick={() => excluir(a)}
                    >
                      <IconTrash size={17} />
                    </button>
                  )}
                </div>
              </footer>
            </article>
          ))}
        </div>
      )}

      {nova && (
        <DiscModal
          onClose={() => setNova(false)}
          onSaved={async (msg) => {
            setAviso(msg);
            await carregar();
          }}
        />
      )}

      {abrindo != null && (
        <DiscModal
          id={abrindo}
          onClose={() => setAbrindo(null)}
          onSaved={async (msg) => {
            setAviso(msg);
            await carregar();
          }}
        />
      )}
    </div>
  );
}

/* ==================== Modal: teste, link e respondentes ==================== */

function DiscModal({
  id,
  onClose,
  onSaved,
}: {
  id?: number;
  onClose: () => void;
  onSaved: (mensagem: string) => Promise<void> | void;
}) {
  /* `idAtual` existe porque o teste recém-criado vira um teste aberto na mesma
     janela: assim que ele nasce, o link e a aba de respondentes aparecem sem o
     RH ter que fechar e abrir de novo. */
  const [idAtual, setIdAtual] = useState<number | null>(id ?? null);
  const [aplicacao, setAplicacao] = useState<DiscAplicacao | null>(null);
  const [nome, setNome] = useState("");
  const [setor, setSetor] = useState("");
  const [cargo, setCargo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [status, setStatus] = useState("Aberta");
  const [token, setToken] = useState<string | null>(null);
  const [respostas, setRespostas] = useState<DiscResposta[]>([]);
  const [aba, setAba] = useState<"teste" | "questoes" | "respondentes">("teste");
  const [lancando, setLancando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  /* O QUESTIONÁRIO DA VAGA (DISC por cargo).
     Lista vazia = este teste usa as 20 questões gerais do papel, que é como
     todo teste funcionava antes desta versão. `origemQuestoes` é só o rastro de
     onde elas vieram — a IA escreveu, o RH montou à mão, ou é o geral. */
  const [questoes, setQuestoes] = useState<QuestaoDiscGabarito[]>([]);
  const [origemQuestoes, setOrigemQuestoes] = useState<OrigemQuestoes>("Geral");
  const [avisosQuestoes, setAvisosQuestoes] = useState<string[]>([]);
  const [msgQuestoes, setMsgQuestoes] = useState<string | null>(null);
  const [gerandoQuestoes, setGerandoQuestoes] = useState(false);

  const carregar = useCallback(async (aplicacaoId: number) => {
    try {
      const a = await api.disc.obter(aplicacaoId);
      setAplicacao(a);
      setNome(a.nome);
      setSetor(a.setor ?? "");
      setCargo(a.cargo ?? "");
      setDescricao(a.descricao ?? "");
      setStatus(a.status);
      setToken(a.token);
      setRespostas(a.respostas ?? []);
      setQuestoes(a.questoes ?? []);
      setOrigemQuestoes(a.questoes_origem ?? "Geral");
      setAvisosQuestoes(a.avisos_questoes ?? []);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Erro ao carregar o teste.");
    }
  }, []);

  useEffect(() => {
    if (id) carregar(id);
  }, [id, carregar]);

  /* O cabeçalho do laudo sai com o que está na tela (inclusive um ajuste ainda
     não salvo), mas com o resto do registro do servidor — id, token, status. */
  const aplicacaoDaFolha = useMemo<DiscAplicacao | null>(() => {
    if (!aplicacao) return null;
    return {
      ...aplicacao,
      nome: nome.trim() || aplicacao.nome,
      setor: setor.trim() || null,
      cargo: cargo.trim() || null,
      descricao: descricao.trim() || null,
    };
  }, [aplicacao, nome, setor, cargo, descricao]);

  async function salvar() {
    setErro(null);
    if (!nome.trim()) {
      setErro("Informe o nome do teste (a vaga ou o motivo da aplicação).");
      return;
    }
    setSalvando(true);
    try {
      const dados = {
        nome: nome.trim(),
        setor: setor.trim(),
        cargo: cargo.trim(),
        descricao: descricao.trim(),
        status,
        /* AS QUESTÕES VÃO NA MESMA GRAVAÇÃO do teste: o RH gera com a IA,
           ajusta o texto e clica em salvar uma vez só. Lista vazia é o pedido
           explícito de voltar ao questionário geral do papel. */
        questoes: questoes.map((q) => ({
          enunciado: q.enunciado.trim(),
          alternativas: q.alternativas.map((a) => ({ texto: a.texto.trim(), fator: a.fator })),
        })),
        questoes_origem: origemQuestoes,
      };
      if (idAtual) {
        await api.disc.atualizar(idAtual, dados);
        await carregar(idAtual);
        await onSaved("Teste DISC atualizado.");
      } else {
        const r = await api.disc.criar(dados);
        setIdAtual(r.id);
        setToken(r.token);
        await carregar(r.id);
        await onSaved("Teste DISC criado — copie o link e mande no WhatsApp do candidato.");
      }
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  /* ---------------- O questionário da vaga (DISC por cargo) ---------------- */

  /* TESTE JÁ RESPONDIDO trava a ESTRUTURA (quantas questões, quais fatores) —
     o texto continua editável. É a mesma regra do servidor, que responde 409:
     mudar o formulário depois de gente ter respondido faria o laudo antigo ser
     lido pelo questionário novo. Aqui ela aparece ANTES, para o RH não gerar um
     questionário inteiro e só descobrir na hora de salvar. */
  const travado = respostas.length > 0;

  /** Uma questão em branco: sempre com as quatro alternativas, uma por fator. */
  function questaoEmBranco(n: number): QuestaoDiscGabarito {
    return {
      n,
      enunciado: "",
      alternativas: FATORES.map((f, i) => ({
        letra: LETRAS[i] as Letra,
        texto: "",
        fator: f,
      })),
    };
  }

  /** Renumera 1..N — o número é o que a resposta do candidato grava. */
  function renumerar(lista: QuestaoDiscGabarito[]): QuestaoDiscGabarito[] {
    return lista.map((q, i) => ({ ...q, n: i + 1 }));
  }

  function alterarEnunciado(i: number, texto: string) {
    setQuestoes((atuais) => atuais.map((q, x) => (x === i ? { ...q, enunciado: texto } : q)));
  }

  function alterarAlternativa(i: number, j: number, campo: "texto" | "fator", valor: string) {
    setQuestoes((atuais) =>
      atuais.map((q, x) =>
        x !== i
          ? q
          : {
              ...q,
              alternativas: q.alternativas.map((a, y) =>
                y !== j ? a : { ...a, [campo]: campo === "fator" ? (valor as Fator) : valor },
              ),
            },
      ),
    );
  }

  /**
   * Pede as questões à IA — o mesmo botão da Triagem de Currículo.
   *
   * SUBSTITUI a lista inteira (a triagem ACRESCENTA, porque lá são perguntas
   * soltas): aqui o questionário é um instrumento fechado, em que os quatro
   * fatores precisam aparecer em proporção parecida. Emendar uma leva de dez
   * questões novas no que já estava na tela desequilibraria o teste sem que o
   * RH percebesse. Por isso a confirmação antes de trocar o que já existe —
   * e nada é gravado enquanto ele não clicar em salvar.
   */
  async function gerarQuestoesIa() {
    if (travado) {
      setErro(
        `Este teste já tem ${respostas.length} resposta(s) corrigida(s): não dá para trocar as ` +
          "questões. Crie um novo teste DISC para usar outro questionário.",
      );
      return;
    }
    if (
      questoes.length > 0 &&
      !confirm("As questões que estão na tela serão substituídas pelas novas. Continuar?")
    ) {
      return;
    }
    setGerandoQuestoes(true);
    setErro(null);
    setMsgQuestoes(null);
    try {
      const r = await api.disc.questoesIa(idAtual ?? 0, { nome, setor, cargo, descricao });
      if (!r.questoes?.length) {
        setErro("A IA não devolveu questões. Tente de novo ou escreva as suas.");
        return;
      }
      setQuestoes(renumerar(r.questoes));
      // "geral" quer dizer que veio o questionário do papel, não o da vaga: a
      // origem tem de acompanhar, senão a tela anunciaria um teste sob medida
      // que ninguém escreveu.
      setOrigemQuestoes(r.fonte === "ia" ? "IA" : "Manual");
      setAvisosQuestoes(r.avisos ?? []);
      setMsgQuestoes(`${r.mensagem} Nada foi gravado ainda — revise e clique em salvar.`);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar as questões.");
    } finally {
      setGerandoQuestoes(false);
    }
  }

  /** Volta ao questionário geral do papel (lista vazia = o padrão de sempre). */
  function usarQuestionarioGeral() {
    if (!confirm(`Usar as ${TOTAL_QUESTOES} questões gerais do papel neste teste?`)) return;
    setQuestoes([]);
    setOrigemQuestoes("Geral");
    setAvisosQuestoes([]);
    setMsgQuestoes(
      `Este teste volta a usar o questionário geral (${TOTAL_QUESTOES} questões). Clique em salvar para valer.`,
    );
  }

  async function copiarLink() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(linkPublico(token));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Navegador sem permissão de área de transferência: o campo continua à
      // mão para copiar manualmente — não vale interromper com um alerta.
      setErro("Não consegui copiar sozinho — selecione o link e copie.");
    }
  }

  /**
   * Abre o WhatsApp com o CONVITE pronto (o RH só escolhe o contato).
   *
   * É a mensagem de quem ainda vai responder — por isso leva o link. Para quem
   * JÁ respondeu existe outra mensagem, no cartão do respondente, sem o link.
   */
  function convidarWhatsApp(telefone?: string | null) {
    if (!token) return;
    const texto =
      `Olá! Somos do RH da SERCAMP.\n\n` +
      `Para seguir no processo de *${nome || "seleção"}*` +
      `${setor ? ` (${setor})` : ""}, responda o teste de perfil comportamental (DISC).\n\n` +
      `São ${questoes.length || TOTAL_QUESTOES} questões rápidas e não existe resposta certa ou errada — ` +
      `responda pensando em como você age no dia a dia:\n${linkPublico(token)}`;
    abrirWhatsApp(telefone, texto);
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-disc" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{idAtual ? nome || "Teste DISC" : "Novo teste DISC"}</h3>
            <p className="modal-sub">
              {questoes.length || TOTAL_QUESTOES} questões
              {questoes.length > 0 ? " escritas para a função" : " do questionário geral"}, correção
              automática pelo gabarito e laudo pronto para imprimir.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        {/* As abas aparecem MESMO ANTES de o teste existir: o RH pode gerar as
            questões da vaga com a IA e só então criar o teste, como já faz na
            triagem. A de respondentes é a única que depende de um teste salvo. */}
        <div className="triagem-abas">
          <button className={aba === "teste" ? "ativa" : ""} onClick={() => setAba("teste")}>
            Teste e link
          </button>
          <button className={aba === "questoes" ? "ativa" : ""} onClick={() => setAba("questoes")}>
            Questões ({questoes.length || TOTAL_QUESTOES})
          </button>
          {idAtual && (
            <button
              className={aba === "respondentes" ? "ativa" : ""}
              onClick={() => setAba("respondentes")}
            >
              Respondentes ({respostas.length})
            </button>
          )}
        </div>

        <div className="modal-body">
          {/* AS DUAS ABAS FICAM MONTADAS o tempo todo: a que não está ativa é
              ESCONDIDA (atributo `hidden`), não desmontada.

              Antes, a lista de respondentes só existia enquanto a aba dela
              estava aberta — e o RH perdia trabalho de verdade: digitava o
              parecer, ia até "Teste e link" conferir o endereço para mandar no
              WhatsApp, voltava e o campo estava em branco. O mesmo acontecia com
              a observação, a caixa do anexo e a prévia do laudo aberta.

              Esconder resolve os quatro de uma vez, porque nada é destruído.
              Guardar rascunhos num Map por id de resposta aqui no modal salvaria
              só os textos — a prévia e o anexo continuariam sumindo — e ainda
              exigiria sincronizar e esvaziar esse Map a cada gravação. Do jeito
              atual não existe rascunho paralelo: o campo do cartão É o valor, e
              depois de "Salvar parecer" ele passa a ser exatamente o texto que o
              servidor devolve (a lista é recarregada logo em seguida), então não
              sobra nada para expirar. O preço é manter os cartões no DOM —
              barato perto de perder o parecer digitado. */}
          <div className="disc-aba" hidden={aba !== "teste"}>
            <div className="doc-grid">
              <label className="field field-wide">
                <span>
                  Nome do teste <span className="req">*</span>
                </span>
                <input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex.: Motorista de munck — obra Monte Mor"
                  autoFocus
                />
              </label>
              <label className="field">
                <span>Setor</span>
                <input
                  value={setor}
                  onChange={(e) => setSetor(e.target.value)}
                  placeholder="Ex.: Operacional"
                />
              </label>
              <label className="field">
                <span>Cargo / função</span>
                <input
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                  placeholder="Ex.: Motorista de munck"
                />
              </label>
              {/* Este texto NÃO é anotação interna: ele aparece na página que o
                  candidato abre pelo link, logo abaixo do nome do teste. O
                  rótulo diz isso com todas as letras para o RH não escrever
                  aqui um comentário de bastidor ("candidato indicado pelo
                  encarregado", "salário abaixo do pedido") achando que só o
                  RH vai ler. */}
              <label className="field field-wide">
                <span>Recado ao candidato (aparece na página do link do teste)</span>
                <textarea
                  rows={2}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Ex.: teste para a vaga de motorista de munck — responda com calma, leva cerca de 10 minutos"
                />
                <small className="muted">
                  Este texto é PÚBLICO: o candidato lê ao abrir o link, logo abaixo do nome do
                  teste. Anotação interna vai no campo <strong>Observação</strong> do
                  respondente, na aba <strong>Respondentes</strong>.
                </small>
              </label>
            </div>

            {token ? (
              <div className="triagem-link">
                <span>Link do candidato</span>
                <div className="triagem-link-linha">
                  <input readOnly value={linkPublico(token)} onFocus={(e) => e.target.select()} />
                  <button type="button" className="btn-secondary btn-sm" onClick={copiarLink}>
                    {copiado ? <IconCheck size={15} /> : <IconLink size={15} />}{" "}
                    {copiado ? "Copiado!" : "Copiar"}
                  </button>
                  <button
                    type="button"
                    className="btn-whats btn-sm"
                    onClick={() => convidarWhatsApp()}
                  >
                    Enviar no WhatsApp
                  </button>
                </div>
                <small className="muted">
                  O mesmo link serve para todos os candidatos deste teste — cada resposta entra na
                  aba <strong>Respondentes</strong>. O candidato não vê o resultado nem o
                  gabarito. Encerre o teste para parar de receber respostas.
                </small>
              </div>
            ) : (
              <p className="muted">
                O link é gerado quando o teste for criado. Depois é só copiar e mandar no
                WhatsApp.
              </p>
            )}

            {idAtual && (
              <label className="field">
                <span>Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="Aberta">Aberta (recebendo respostas)</option>
                  <option value="Encerrada">Encerrada</option>
                </select>
              </label>
            )}
          </div>

          {/* ---------- AS QUESTÕES DA VAGA (o DISC por cargo) ----------
              Fica montada junto com as outras abas (só escondida) pelo mesmo
              motivo dos respondentes: sair daqui para conferir o link e voltar
              não pode apagar um questionário inteiro que ainda não foi salvo. */}
          <div className="disc-aba" hidden={aba !== "questoes"}>
            <div className="triagem-perguntas-head">
              <span>
                Questões do teste{" "}
                <small className="muted">
                  ({questoes.length || TOTAL_QUESTOES}
                  {questoes.length === 0 ? " — questionário geral" : ""})
                </small>
              </span>
              <div>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={travado}
                  onClick={() =>
                    setQuestoes((atuais) => [...atuais, questaoEmBranco(atuais.length + 1)])
                  }
                >
                  <IconPlus size={15} /> Questão
                </button>
                <button
                  type="button"
                  className="btn-ia btn-sm"
                  onClick={gerarQuestoesIa}
                  disabled={gerandoQuestoes || travado || (!nome.trim() && !cargo.trim())}
                  title={
                    travado
                      ? "O teste já tem respostas corrigidas"
                      : nome.trim() || cargo.trim()
                        ? "Escrever as questões do DISC para esta função"
                        : "Informe o cargo (ou o nome do teste) primeiro"
                  }
                >
                  <IconSparkles size={15} />{" "}
                  {gerandoQuestoes ? "Escrevendo…" : "Gerar com IA"}
                </button>
              </div>
            </div>

            {travado && (
              <p className="muted">
                Este teste já tem <strong>{respostas.length}</strong> resposta(s) corrigida(s):
                o questionário fica travado para os laudos continuarem batendo com o que essas
                pessoas responderam. Para aplicar outro questionário, crie um novo teste DISC.
              </p>
            )}

            {msgQuestoes && <div className="form-ok">{msgQuestoes}</div>}

            {avisosQuestoes.length > 0 && (
              <ul className="disc-avisos">
                {avisosQuestoes.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            )}

            {questoes.length === 0 ? (
              <p className="muted">
                Este teste usa o <strong>questionário geral</strong> — as {TOTAL_QUESTOES} questões
                do papel, iguais para qualquer vaga. Clique em <strong>Gerar com IA</strong> para o
                sistema escrever as questões do <strong>cargo</strong> (situações do dia a dia da
                função), revise o que quiser e salve. As questões ficam guardadas neste teste e são
                elas que o candidato responde pelo link.
              </p>
            ) : (
              <>
                <p className="muted">
                  Cada questão tem quatro alternativas e cada alternativa soma um fator
                  (D, I, S ou C) — é o gabarito, e ele <strong>não</strong> vai para a tela do
                  candidato. Edite o texto à vontade; troque o fator só se souber o que está
                  fazendo.
                </p>
                <ol className="disc-editor">
                  {questoes.map((q, i) => (
                    <li className="disc-editor-questao" key={i}>
                      <div className="disc-editor-topo">
                        <span className="disc-num">{q.n}</span>
                        <textarea
                          rows={2}
                          value={q.enunciado}
                          onChange={(e) => alterarEnunciado(i, e.target.value)}
                          placeholder="Situação do dia a dia da função — como a pessoa normalmente age?"
                        />
                        <button
                          type="button"
                          className="icon-btn icon-btn-danger"
                          title="Remover questão"
                          disabled={travado}
                          onClick={() =>
                            setQuestoes((atuais) =>
                              renumerar(atuais.filter((_, x) => x !== i)),
                            )
                          }
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                      <div className="disc-editor-alts">
                        {q.alternativas.map((a, j) => (
                          <div className="disc-editor-alt" key={a.letra}>
                            <span className="disc-alt-letra">{a.letra}</span>
                            <input
                              value={a.texto}
                              onChange={(e) => alterarAlternativa(i, j, "texto", e.target.value)}
                              placeholder="Como a pessoa age nessa situação"
                            />
                            <select
                              value={a.fator}
                              disabled={travado}
                              title="Fator que esta alternativa soma no resultado"
                              onChange={(e) => alterarAlternativa(i, j, "fator", e.target.value)}
                            >
                              {FATORES.map((f) => (
                                <option key={f} value={f}>
                                  {f} — {FATOR_INFO[f].nome}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="triagem-novo-acoes">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={travado}
                    onClick={usarQuestionarioGeral}
                  >
                    Voltar ao questionário geral
                  </button>
                </div>
              </>
            )}
          </div>

          {idAtual != null && (
            <div className="disc-aba" hidden={aba !== "respondentes"}>
              <div className="triagem-perguntas-head">
                <span>
                  Respondentes <small className="muted">({respostas.length})</small>
                </span>
                <div>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={() => setLancando((v) => !v)}
                  >
                    <IconPlus size={15} /> {lancando ? "Cancelar" : "Lançar teste do papel"}
                  </button>
                </div>
              </div>

              {/* O teste também é aplicado impresso, na entrevista presencial:
                  o RH digita aqui o que está no papel e a correção é a mesma. */}
              {lancando && (
                <LancarPapelForm
                  aplicacaoId={idAtual}
                  /* O cartão de papel que o RH está digitando é o DESTE teste —
                     o questionário GRAVADO, não o rascunho da aba Questões. */
                  questoes={questoesDoTeste(aplicacao)}
                  onCancelar={() => setLancando(false)}
                  onLancado={async (msg) => {
                    setLancando(false);
                    await carregar(idAtual);
                    await onSaved(msg);
                  }}
                />
              )}

              {respostas.length === 0 ? (
                <p className="muted">
                  Ninguém respondeu ainda. Mande o link no WhatsApp — o resultado aparece aqui já
                  corrigido. Se o candidato respondeu no papel, use{" "}
                  <strong>Lançar teste do papel</strong>.
                </p>
              ) : (
                <div className="triagem-candidatos">
                  {respostas.map((r) => (
                    <RespondenteCard
                      key={r.id}
                      aplicacao={aplicacaoDaFolha}
                      resposta={r}
                      onSalvo={async (msg) => {
                        await carregar(idAtual);
                        await onSaved(msg);
                      }}
                      onRemover={async (resp) => {
                        if (!confirm(`Apagar o teste de ${resp.candidato}?`)) return;
                        try {
                          const removida = await api.disc.removerResposta(idAtual, resp.id);
                          setRespostas((atuais) => atuais.filter((x) => x.id !== resp.id));
                          await onSaved(removida.mensagem ?? "Resposta removida.");
                        } catch (e) {
                          alert(e instanceof Error ? e.message : "Erro ao remover.");
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>
            Fechar
          </button>
          {/* O mesmo botão vale nas duas abas de edição: as questões da vaga são
              gravadas junto com o teste, numa chamada só. */}
          {aba !== "respondentes" && (
            <button type="button" className="btn-primary" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : idAtual ? "Salvar alterações" : "Criar e gerar link"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== O gráfico D / I / S / C ==================== */

export function BarrasDisc({
  placar,
  respondidas,
  /** Quantas questões o teste tinha — é o que define as faixas (ver faixaDisc). */
  totalQuestoes,
}: {
  placar: PlacarDisc;
  respondidas: number;
  totalQuestoes: number;
}) {
  /* A barra é proporcional ao TOTAL RESPONDIDO, e não às 20 questões: quando o
     teste vem incompleto (lançado do papel pela metade) o gráfico ficaria todo
     espremido e daria a impressão falsa de que a pessoa "pontuou pouco" em
     tudo. A comparação entre os quatro fatores — que é o que o DISC lê —
     continua verdadeira, e o número exato fica ao lado da barra. */
  const somado = FATORES.reduce((s, f) => s + placar[f], 0);
  const total = Math.max(1, respondidas || somado);

  return (
    <div className="disc-fatores">
      {FATORES.map((f) => {
        const valor = placar[f];
        const info = FATOR_INFO[f];
        return (
          <div className="disc-fator" key={f}>
            <div className="disc-fator-topo">
              <strong>{f}</strong>
              <span>{info.nome}</span>
              <b>{valor}</b>
            </div>
            <div className="disc-fator-trilho">
              {/* A cor sai do modelo (a paleta do DISC no papel) e não do CSS:
                  é dado do teste, e o laudo impresso usa exatamente a mesma. */}
              <span
                className="disc-fator-fill"
                style={{ width: `${Math.round((valor / total) * 100)}%`, background: info.cor }}
              />
            </div>
            <small className="disc-fator-faixa">{faixaDisc(valor, totalQuestoes)}</small>
          </div>
        );
      })}
    </div>
  );
}

/* ==================== Cartão de um respondente ==================== */

function RespondenteCard({
  aplicacao,
  resposta,
  onSalvo,
  onRemover,
}: {
  aplicacao: DiscAplicacao | null;
  resposta: DiscResposta;
  onSalvo: (mensagem: string) => Promise<void> | void;
  onRemover: (r: DiscResposta) => Promise<void>;
}) {
  /* O questionário GRAVADO na aplicação — o que este candidato respondeu. Vem
     de `aplicacao`, e não do rascunho que o RH esteja editando na aba Questões:
     o cartão e o laudo têm de mostrar o teste que a pessoa fez. */
  const questoes = useMemo(() => questoesDoTeste(aplicacao), [aplicacao]);
  const totalQuestoes = questoes.length;
  const podeExcluir = usePodeExcluir(); // só admin apaga o teste de um candidato
  const [aberto, setAberto] = useState(false);
  const [parecer, setParecer] = useState(resposta.parecer ?? "");
  const [observacao, setObservacao] = useState(resposta.observacao ?? "");
  /* De onde veio a sugestão que está no campo: "ia" (o modelo respondeu) ou
     "padrao" (o texto determinístico do servidor). É CÓDIGO da API, não texto
     de tela — quem traduz é a mensagem lá embaixo. */
  const [fonteSugestao, setFonteSugestao] = useState<string | null>(null);
  const [previa, setPrevia] = useState(false);
  /* O anexo com as 20 respostas sai numa segunda página (o laudo quebra a
     página antes dele). Quase toda impressão é só a folha do resultado, então
     ele entra por escolha do RH — desmarcado por padrão. */
  const [incluirRespostas, setIncluirRespostas] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const resultado = useMemo(() => resultadoDaResposta(resposta), [resposta]);
  const leitura = useMemo(() => leituraDisc(resultado, totalQuestoes), [resultado, totalQuestoes]);
  /* `useMemo` para a lista não trocar de identidade a cada render quando ela
     vem vazia: a prévia depende dela e o iframe recarregaria à toa. */
  const marcadas = useMemo(() => resposta.respostas ?? [], [resposta]);
  const contato = [resposta.telefone, resposta.email].filter(Boolean).join(" · ");

  /**
   * Tudo que o laudo precisa (a folha com o cabeçalho da SERCAMP é montada em
   * src/disc-folha.ts).
   *
   * O parecer e a observação saem do que está NA TELA, e não do que está
   * gravado: o RH costuma pedir a sugestão da IA, ajustar o texto e imprimir na
   * mesma hora — o papel tem que sair com o que ele acabou de ler.
   *
   * É `useCallback` porque a pré-visualização depende dele: a folha na tela tem
   * que ser remontada assim que o parecer, a observação ou o anexo mudarem.
   */
  const dadosDaFolha = useCallback(
    (): DadosFolhaDisc => ({
      candidato: resposta.candidato,
      // O título do laudo é "Resultado do Disc — <FUNÇÃO> — <nome>", e a folha
      // escolhe `cargo` antes de `vaga`. Então o cargo vai puro: colapsar o nome
      // da aplicação aqui dentro fazia a obra/o contrato ("Motorista de munck —
      // obra Monte Mor", que é o próprio placeholder do campo) virar a função no
      // cabeçalho. O nome da aplicação é o último recurso, depois da vaga que o
      // candidato escreveu.
      cargo: aplicacao?.cargo ?? null,
      vaga: resposta.vaga_pretendida ?? aplicacao?.nome ?? null,
      email: resposta.email,
      telefone: resposta.telefone,
      placar: resultado.placar,
      perfil: resultado.perfil,
      perfilNome: resultado.perfilNome,
      parecer,
      observacao,
      respondidoEm: resposta.respondido_em,
      avaliador: resposta.parecer_por,
      // Sem a marcação do RH o laudo sai com uma folha só (a folha só imprime o
      // anexo quando a lista chega preenchida).
      respostas: incluirRespostas ? marcadas : [],
      /* O QUESTIONÁRIO vai junto: é ele que dá o total ("9 de 12"), as faixas e
         o enunciado que o anexo imprime ao lado de cada marcação. Sem isto, o
         laudo de um teste feito para o cargo sairia contado sobre as 20 do
         papel — e com as frases do formulário errado no anexo. */
      questoes,
      origemQuestoes: aplicacao?.questoes_origem ?? null,
    }),
    [resposta, aplicacao, resultado, parecer, observacao, incluirRespostas, marcadas, questoes],
  );

  /**
   * A PRÉ-VISUALIZAÇÃO é montada na hora, e não guardada numa string de estado.
   *
   * Congelada, ela mostrava a folha de quando o botão "Ver laudo" foi clicado:
   * o RH abria a prévia, pedia o parecer da IA e continuava vendo a versão
   * antiga — parecia que a IA não tinha entrado no laudo. Agora o estado é só
   * "está aberta?" e o HTML acompanha parecer, observação e anexo.
   */
  const dadosAgora = useMemo(() => dadosDaFolha(), [dadosDaFolha]);

  /* ...só que o iframe RECARREGA A PÁGINA INTEIRA a cada troca de `srcDoc`, e
     com a folha remontada a cada tecla a prévia piscava enquanto o RH escrevia o
     parecer. Por isso o que alimenta a prévia é a folha com um atraso curto: o
     textarea continua respondendo na hora (é ele que manda no texto) e o iframe
     espera 400 ms de silêncio para se redesenhar. A primeira montagem, logo
     depois do clique em "Ver laudo", entra sem espera — ali não há o que piscar
     e a pessoa está esperando a folha aparecer. */
  const [dadosPrevia, setDadosPrevia] = useState<DadosFolhaDisc | null>(null);
  const previaJaMontada = useRef(false);

  useEffect(() => {
    if (!previa) {
      previaJaMontada.current = false;
      setDadosPrevia(null);
      return;
    }
    if (!previaJaMontada.current) {
      previaJaMontada.current = true;
      setDadosPrevia(dadosAgora);
      return;
    }
    const timer = setTimeout(() => setDadosPrevia(dadosAgora), 400);
    return () => clearTimeout(timer);
  }, [previa, dadosAgora]);

  const previaLaudo = useMemo(
    () => (dadosPrevia ? montarFolhaDisc(dadosPrevia) : null),
    [dadosPrevia],
  );

  /** A IA SUGERE o parecer — nada é gravado até o RH salvar. */
  async function pedirIa() {
    if (
      parecer.trim() &&
      !confirm(
        "A sugestão da IA vai substituir o texto que está no campo.\n\n" +
          "Nada foi gravado ainda — o parecer salvo só muda quando você clicar em " +
          '"Salvar parecer". Continuar?',
      )
    )
      return;
    setGerando(true);
    setErro(null);
    try {
      const r = await api.disc.parecerIa(resposta.id);
      if (!r.parecer?.trim()) {
        setErro("A IA não devolveu um parecer. Tente de novo ou escreva o seu.");
        return;
      }
      setParecer(r.parecer);
      setFonteSugestao(r.fonte || "padrao");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar o parecer.");
    } finally {
      setGerando(false);
    }
  }

  async function salvarParecer() {
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.disc.parecer(resposta.id, {
        parecer: parecer.trim(),
        observacao: observacao.trim(),
      });
      setFonteSugestao(null); // deixou de ser sugestão: agora está gravado
      await onSalvo(r.mensagem ?? "Parecer salvo.");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar o parecer.");
    } finally {
      setSalvando(false);
    }
  }

  /**
   * Abre a janela de impressão. Quando o navegador bloqueia o pop-up, a folha
   * devolve a mensagem em vez de falhar calada — o aviso vai para a tela, senão
   * o RH clica, nada acontece e ele conclui que o sistema quebrou.
   */
  function imprimir() {
    const falha = imprimirFolhaDisc(dadosDaFolha());
    setErro(falha ? `${falha} Enquanto isso, use "Ver laudo" aqui na tela.` : null);
  }

  /** Abre/fecha a prévia — o HTML sai do `previaLaudo` (com o atraso curto). */
  function verLaudo() {
    setErro(null);
    setPrevia((aberta) => !aberta);
  }

  /**
   * WhatsApp de quem JÁ RESPONDEU: mensagem de RETORNO, sem o link do teste.
   *
   * O convite (com o endereço) fica no bloco do link, na aba "Teste e link".
   * Mandar o convite para quem já respondeu fazia o candidato refazer o teste
   * achando que a primeira resposta se perdeu — e o segundo envio entra como
   * outro respondente na lista.
   */
  function avisarNoWhatsApp() {
    const nome = primeiroNome(resposta.candidato);
    const funcao =
      String(resposta.vaga_pretendida ?? "").trim() ||
      String(aplicacao?.cargo ?? "").trim() ||
      "";
    const texto =
      `Olá${nome ? ` ${nome}` : ""}! Aqui é o RH da SERCAMP.\n\n` +
      `Recebemos suas respostas do teste de perfil comportamental (DISC)` +
      `${funcao ? ` para *${funcao}*` : ""}. Obrigado pelo tempo que você dedicou!\n\n` +
      `O resultado está com o nosso RH e entramos em contato com o próximo passo do processo.`;
    abrirWhatsApp(resposta.telefone, texto);
  }

  return (
    <article className="triagem-candidato">
      <header onClick={() => setAberto(!aberto)}>
        <div>
          <strong>{resposta.candidato}</strong>
          <small className="muted">
            {" "}
            · {contato || "sem contato"} · {quando(resposta.respondido_em)}
            {resposta.origem === "RH"
              ? ` · lançado pelo RH${resposta.criado_por ? ` (${resposta.criado_por})` : ""}`
              : " · respondeu pelo link"}
            {resposta.vaga_pretendida ? ` · vaga: ${resposta.vaga_pretendida}` : ""}
          </small>
        </div>
        {!resposta.parecer && <span className="pill pill-warn">sem parecer</span>}
        <span className="pill disc-pill-perfil">{resultado.perfil}</span>
      </header>

      <div className="triagem-candidato-corpo">
        <p className="disc-perfil">
          Perfil predominante: <strong>{resultado.perfil}</strong> — {resultado.perfilNome}
          <small className="muted">
            {" "}
            · {resultado.respondidas} de {totalQuestoes} questões respondidas
          </small>
        </p>

        <BarrasDisc
          placar={resultado.placar}
          respondidas={resultado.respondidas}
          totalQuestoes={totalQuestoes}
        />

        <button
          type="button"
          className="btn-secondary btn-sm disc-expandir"
          onClick={() => setAberto(!aberto)}
        >
          {aberto ? "Ocultar leitura e respostas" : "Ver leitura e o que ele marcou"}
        </button>

        {aberto && (
          <div className="disc-leitura">
            <h4>Leitura do resultado</h4>
            <ul>
              {leitura.caracteristicas.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>

            {leitura.atencao.length > 0 && (
              <>
                <h4>Pontos de atenção</h4>
                <ul className="disc-atencao">
                  {leitura.atencao.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </>
            )}

            <h4>O que o candidato marcou</h4>
            {marcadas.length === 0 ? (
              <p className="muted">Nenhuma questão marcada.</p>
            ) : (
              <ol className="disc-marcadas">
                {marcadas.map((m) => (
                  <li key={m.q}>
                    <span className="disc-marcada-n">{m.q}</span>
                    <span className="disc-marcada-letra">{String(m.alternativa).toUpperCase()}</span>
                    <span className="disc-marcada-txt">
                      <em>{enunciadoDaQuestao(m.q, questoes)}</em>
                      {textoAlternativa(m.q, m.alternativa, questoes)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <div className="disc-parecer">
          <label className="field field-wide">
            <span>
              Parecer do RH{" "}
              {resposta.parecer_por && (
                <small className="muted">
                  (salvo por {resposta.parecer_por} em {quando(resposta.parecer_em)})
                </small>
              )}
            </span>
            <textarea
              rows={4}
              value={parecer}
              onChange={(e) => setParecer(e.target.value)}
              placeholder="Como o perfil conversa com a função, o que observar na entrevista e a recomendação do RH"
            />
          </label>

          {/* O que vem da API é um código ("ia" | "padrao"), não uma frase:
              escrito cru, a tela dizia "Sugestão da ok". Quando não foi a IA, o
              RH precisa saber que aquele texto é o modelo padrão do sistema —
              é o sinal de que vale reescrever com o olhar dele. */}
          {fonteSugestao && (
            <p className="disc-sugestao">
              <IconSparkles size={15} />{" "}
              {fonteSugestao === "ia" ? "Sugestão da IA" : "Sugestão padrão (sem IA)"} — revise o
              texto e clique em <strong>Salvar parecer</strong>. Nada foi gravado ainda.
            </p>
          )}

          <label className="field field-wide">
            <span>Observação</span>
            <input
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex.: aplicado na entrevista presencial; teste respondido no papel"
            />
          </label>

          {/* Escolha do RH, e não regra fixa: o laudo de uma folha é o que vai
              para a pasta do candidato; o anexo com as 20 questões só é pedido
              na devolutiva, quando a pessoa quer ver o que marcou. */}
          <label className="disc-anexo-check">
            <input
              type="checkbox"
              checked={incluirRespostas}
              onChange={(e) => setIncluirRespostas(e.target.checked)}
              disabled={marcadas.length === 0}
            />
            <span>
              Incluir as respostas marcadas{" "}
              <small className="muted">
                {marcadas.length === 0
                  ? "(este teste não tem marcações gravadas)"
                  : "— vai como anexo, numa segunda página"}
              </small>
            </span>
          </label>

          <div className="triagem-avaliar-acoes">
            {podeExcluir && (
              <button
                type="button"
                className="btn-secondary btn-sm btn-danger-text"
                onClick={() => onRemover(resposta)}
                title="Apagar o teste deste candidato"
              >
                <IconTrash size={15} /> Remover
              </button>
            )}
            {/* O botão fica SEMPRE visível, com ou sem telefone gravado.
                O campo é opcional na página pública, e escondê-lo tirava a ação
                justamente do cartão de quem não informou o número — o RH nem
                via que havia esse retorno a dar. Desabilitar seria a mesma
                cegueira com outro nome: o botão apareceria morto, sem caminho.
                Sem número o wa.me abre o WhatsApp com a mensagem já escrita e o
                RH escolhe o contato na agenda (o número quase sempre está no
                currículo ou na conversa da vaga); o rótulo e o title avisam
                disso ANTES do clique, então ninguém é pego de surpresa. */}
            <button
              type="button"
              className="btn-whats btn-sm"
              onClick={avisarNoWhatsApp}
              title={
                resposta.telefone
                  ? "Avisa o candidato que as respostas chegaram (sem o link do teste)"
                  : "Este candidato não informou o WhatsApp — abre o app com a mensagem pronta para você escolher o contato"
              }
            >
              {resposta.telefone ? "WhatsApp" : "WhatsApp (escolher contato)"}
            </button>
            <button
              type="button"
              className="btn-ia btn-sm"
              onClick={pedirIa}
              disabled={gerando}
              title="A IA lê o placar e sugere o parecer — você revisa antes de salvar"
            >
              <IconSparkles size={15} /> {gerando ? "Gerando…" : "Parecer com IA"}
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={verLaudo}>
              <IconEye size={15} /> {previa ? "Fechar laudo" : "Ver laudo"}
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={imprimir}>
              <IconClipboard size={15} /> Imprimir resultado
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={salvarParecer}
              disabled={salvando}
            >
              {salvando ? "Salvando…" : "Salvar parecer"}
            </button>
          </div>

          {erro && <div className="form-error">{erro}</div>}
        </div>

        {previaLaudo && (
          <iframe
            className="disc-previa"
            title={`Laudo DISC de ${resposta.candidato}`}
            srcDoc={previaLaudo}
          />
        )}
      </div>
    </article>
  );
}

/* ==================== O RH digita o teste respondido no papel ==================== */

/**
 * LANÇAR TESTE DO PAPEL.
 *
 * O formulário impresso continua sendo usado na entrevista presencial. Aqui o
 * RH digita olhando a folha, então a grade é COMPACTA de propósito: uma linha
 * por questão e quatro botões A/B/C/D, sem campo de texto e sem rolagem longa.
 * Clicar de novo na mesma letra limpa a marcação (foi engano ao digitar).
 *
 * Pode salvar incompleto: às vezes o papel volta com questões em branco, e o
 * gabarito simplesmente não soma o que não foi marcado.
 */
function LancarPapelForm({
  aplicacaoId,
  questoes,
  onCancelar,
  onLancado,
}: {
  aplicacaoId: number;
  /** O questionário DESTE teste (o da vaga, ou as 20 do papel). */
  questoes: QuestaoExibida[];
  onCancelar: () => void;
  onLancado: (mensagem: string) => Promise<void> | void;
}) {
  const [candidato, setCandidato] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [vaga, setVaga] = useState("");
  const [observacao, setObservacao] = useState("");
  const [marcadas, setMarcadas] = useState<Record<number, Letra>>({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const feitas = Object.keys(marcadas).length;
  const total = questoes.length;

  function marcar(q: number, letra: Letra) {
    setMarcadas((atuais) => {
      const copia = { ...atuais };
      if (copia[q] === letra) delete copia[q];
      else copia[q] = letra;
      return copia;
    });
  }

  async function salvar() {
    setErro(null);
    if (!candidato.trim()) {
      setErro("Informe o nome do candidato.");
      return;
    }
    if (feitas === 0) {
      setErro("Marque pelo menos uma questão do papel.");
      return;
    }
    setSalvando(true);
    try {
      const respostas = Object.entries(marcadas)
        .map(([q, alternativa]) => ({ q: Number(q), alternativa }))
        .sort((a, b) => a.q - b.q);

      const r = await api.disc.lancarResposta(aplicacaoId, {
        candidato: candidato.trim(),
        email: email.trim(),
        telefone: telefone.trim(),
        vaga_pretendida: vaga.trim(),
        observacao: observacao.trim(),
        respostas,
      });
      await onLancado(r.mensagem ?? `Teste de ${candidato.trim()} lançado e corrigido.`);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível lançar o teste.");
      setSalvando(false);
    }
  }

  return (
    <div className="triagem-novo-candidato">
      <p className="muted">
        Digite o que está marcado na folha impressa. A correção é a mesma de quem responde pelo
        link, e o candidato entra na lista como <strong>lançado pelo RH</strong>.
      </p>

      <div className="doc-grid">
        <label className="field field-wide">
          <span>
            Nome do candidato <span className="req">*</span>
          </span>
          <input
            value={candidato}
            onChange={(e) => setCandidato(e.target.value)}
            placeholder="Nome e sobrenome"
            autoFocus
          />
        </label>
        <label className="field">
          <span>WhatsApp</span>
          <input
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="(19) 90000-0000"
            inputMode="tel"
          />
        </label>
        <label className="field">
          <span>E-mail</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="candidato@email.com"
            inputMode="email"
          />
        </label>
        <label className="field field-wide">
          <span>Vaga pretendida</span>
          <input
            value={vaga}
            onChange={(e) => setVaga(e.target.value)}
            placeholder="Ex.: Motorista de munck"
          />
        </label>
        <label className="field field-wide">
          <span>Observação</span>
          <input
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Ex.: aplicado na entrevista do dia 12/08"
          />
        </label>
      </div>

      <div className="disc-grade-head">
        <span>Respostas do papel</span>
        <small className={feitas === total ? "disc-grade-ok" : "muted"}>
          {feitas} de {total} marcadas
          {feitas < total ? " — pode salvar assim mesmo" : ""}
        </small>
      </div>

      <ol className="disc-grade">
        {questoes.map((q) => (
          <li className="disc-grade-linha" key={q.n}>
            <span className="disc-grade-n">{q.n}</span>
            <span className="disc-grade-txt" title={q.enunciado}>
              {q.enunciado}
            </span>
            <span className="disc-letras">
              {/* As letras saem das ALTERNATIVAS da questão, e não de A–D fixo:
                  um questionário escrito para a vaga pode ter uma questão com
                  três opções, e um botão "D" sem alternativa por trás gravaria
                  uma marcação que o servidor descarta na correção. */}
              {q.alternativas.map((alt) => {
                const l = alt.letra as Letra;
                return (
                  <button
                    type="button"
                    key={l}
                    className={`disc-letra${marcadas[q.n] === l ? " on" : ""}`}
                    /* O texto da alternativa fica no title: o RH confere sem ter
                       que procurar a frase no papel. */
                    title={alt.texto}
                    onClick={() => marcar(q.n, l)}
                  >
                    {l}
                  </button>
                );
              })}
            </span>
          </li>
        ))}
      </ol>

      {erro && <div className="form-error">{erro}</div>}

      <div className="triagem-novo-acoes">
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={onCancelar}
          disabled={salvando}
        >
          Cancelar
        </button>
        <button type="button" className="btn-primary btn-sm" onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar e corrigir"}
        </button>
      </div>
    </div>
  );
}
